import { Button } from 'reactstrap';
import { createHashHistory } from 'history';
import { getValue, setValue, appendPath } from '@truqc/content-path';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { enabledSelector, newPathSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { getContent } from '../selectors/simpleSelectors';
const pendingChangeMessage = 'There are pending changes for this report, please save before attempting to create an issue again.';
const noIssueTypeMessage = 'No issue type exists for this customer.  This has most likely not been setup yet, please contact support to do so.';
const postWarning = notificationActions => new Promise((resolve, reject) => {
  notificationActions.info({
    id: 'confirm_issue_navigation',
    message: 'Continuing will create a new issue with the information provided. Do you wish to proceed?',
    rightButtonLabel: 'Yes',
    rightButtonAction: () => resolve(),
    leftButtonLabel: 'No',
    leftButtonAction: () => reject()
  });
});
const pathsToRegenerate = ['itemCollection', 'ndtCollection'];
const hashHistory = createHashHistory();
const regenerateUUIDs = patch => {
  const stringPatch = JSON.stringify(patch);
  /* eslint-disable-next-line prefer-regex-literals */
  const regex = new RegExp(/[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}/ig);
  return JSON.parse(stringPatch.replace(regex, () => uuid().toUpperCase()));
};
const NewIssueButton = ({
  activeID,
  activeIssues: _activeIssues = [],
  activeReportTypeID,
  content,
  enabled,
  hasPendingChanges,
  issueActions,
  map,
  notificationActions,
  path,
  reportTypeIssueType: {
    indexQuery,
    list
  },
  reportTypeIssueTypeActions,
  title
}) => {
  useEffect(() => {
    reportTypeIssueTypeActions.serverIndex(indexQuery);
  }, [reportTypeIssueTypeActions, indexQuery]);
  const reportTypeIssueType = list.find(type => type.reportTypeID === activeReportTypeID);
  const handleClick = async () => {
    if (hasPendingChanges) {
      return notificationActions.info({
        rightButtonLabel: 'Ok',
        id: 'stop_issue_navigation',
        message: pendingChangeMessage
      });
    }
    if (!reportTypeIssueType) {
      return notificationActions.info({
        rightButtonLabel: 'Ok',
        id: 'stop_issue_navigation',
        message: noIssueTypeMessage
      });
    }
    try {
      await postWarning(notificationActions);
    } catch (err) {
      return err;
    }
    const id = uuid();
    const totalPartialIssues = _activeIssues.filter(issue => !issue.reportAnnotationID && !issue.worldMapAnnotationID).length;
    let newContent = {
      id,
      label: (totalPartialIssues + 1).toString(),
      reportID: activeID,
      issueTypeID: reportTypeIssueType.issueTypeID,
      version: reportTypeIssueType.version
    };
    Object.keys(map || {}).forEach(sourcePath => {
      let data = getValue(appendPath(path || '/', sourcePath), content);
      const shouldRegenerateUUIDs = pathsToRegenerate.some(regenPath => map[sourcePath].includes(regenPath));
      if (shouldRegenerateUUIDs && data) {
        data = regenerateUUIDs(data);
      }
      newContent = setValue(map[sourcePath], newContent, data);
    });
    return issueActions.serverCreateIssue({
      id,
      entity: 'Issue',
      patch: newContent
    }, savedID => {
      hashHistory.push(`/issues/${savedID}`);
    });
  };
  return /*#__PURE__*/React.createElement(Button, {
    disabled: !enabled,
    block: true,
    onClick: handleClick,
    className: "t-rb-new-issue-button tqc-control"
  }, title);
};
NewIssueButton.propTypes = {
  activeID: PropTypes.string.isRequired,
  activeIssues: PropTypes.arrayOf(PropTypes.shape({})),
  activeReportTypeID: PropTypes.string.isRequired,
  content: PropTypes.shape({}).isRequired,
  enabled: PropTypes.bool.isRequired,
  hasPendingChanges: PropTypes.bool.isRequired,
  issueActions: PropTypes.shape({
    serverCreateIssue: PropTypes.func
  }).isRequired,
  map: PropTypes.shape({}).isRequired,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired,
  path: PropTypes.string.isRequired,
  reportTypeIssueType: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportTypeIssueTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  title: PropTypes.string.isRequired
};
export { NewIssueButton as UncomposedNewIssueButton };
export default entityComposition(NewIssueButton, ['reportTypeIssueType'], {
  activeID: state => state.report.active.id || state.issue.active.id,
  activeIssues: state => state.report.active.issueObjects,
  activeReportTypeID: state => state.report.active.reportTypeID,
  enabled: enabledSelector,
  hasPendingChanges: state => state.report.pendingChanges || state.issue.pendingChanges,
  path: newPathSelector,
  content: getContent
}, ['issue', 'notification']);